import useDebounce from 'shared/composables/debounce';
import usePersonalAppSettings from '@/composables/logged-in-user/personal-app-settings';
import usePrintMode from '@/composables/export/print-mode';
import { EVENTS, MAX_SIDEBAR_WIDTH, MIN_SIDEBAR_WIDTH, ROUTES_WITHOUT_SIDEBAR } from '@/lib/constants';
import { EventBus } from '@/lib/event-bus';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { constrainWidth } from '@/lib/side-peek';
import { personalAppSettings as personalAppSettingsConfig } from 'shared/api/query/configs.json';
import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { goalViewMode as viewMode } from 'shared/constants.json';

const MIN_CONTENT_WIDTH = 430;

export default function useSidebarSvc(loggedInUser) {
  const route = useRoute();
  const router = useRouter();
  const store = useStore();
  const { debounce } = useDebounce();
  const overwriteDesktopSidebarHidden = ref(false);

  const desktopSidebarHidden = computed(() => {
    if (overwriteDesktopSidebarHidden.value || store.state.breakpoint.smAndDown) {
      return true;
    }
    return personalAppSettings.value.desktopSidebarHidden;
  });

  const constrainSidebarWidth = (width) => Math.min(MAX_SIDEBAR_WIDTH, Math.max(width, MIN_SIDEBAR_WIDTH));

  const { personalAppSettings, updateSingle } = usePersonalAppSettings(loggedInUser);

  const dragging = ref(false);
  const draggingWidth = ref(0);
  const sidebarStartX = ref(0);

  const savedSidebarWidth = computed(() => constrainSidebarWidth(personalAppSettings.value.mainSidebarWidth));

  const effectiveSidebarWidthForLayout = computed(() => {
    if (desktopSidebarHidden.value || ROUTES_WITHOUT_SIDEBAR.includes(route.name)) {
      return 0;
    }
    return sidebarWidth.value;
  });

  const sidebarWidth = computed(() => {
    if (dragging.value) {
      return constrainSidebarWidth(draggingWidth.value);
    }
    return savedSidebarWidth.value;
  });

  const updateHideDesktopSideBar = (value) => {
    let updateValue = value;
    if (overwriteDesktopSidebarHidden.value || (!overwriteDesktopSidebarHidden.value && desktopSidebarHidden.value)) {
      const query = { ...route.query };
      delete query.goalId;
      router.push({ ...route, query });
      updateValue = false;
    }

    updateSingle({
      uid: personalAppSettings.value.uid,
      [personalAppSettingsConfig.edges.desktopSidebarHidden]: updateValue,
    });
  };

  const sidePeekActive = computed(() => personalAppSettings.value?.goalViewMode === viewMode.sidePeek && route.query.goalId !== undefined);
  const sidePeekWidth = computed(() => {
    if (!sidePeekActive.value) {
      return 0;
    }
    if (personalAppSettings.value?.goalSidePeekWidth === undefined) {
      return 0;
    }
    return constrainWidth(personalAppSettings.value.goalSidePeekWidth, windowWidth.value);
  });
  const windowWidth = ref(window.innerWidth);
  const padding = computed(() => {
    if (store.state.breakpoint.smAndDown) {
      return 0;
    }
    return 96 * 2;
  });

  const contentWidth = computed(() => windowWidth.value - sidebarWidth.value - padding.value);
  const containerWidthWatch = computed(() => windowWidth.value - sidebarWidth.value - sidePeekWidth.value);

  const containerWidth = computed(() => windowWidth.value - effectiveSidebarWidthForLayout.value - sidePeekWidth.value);

  const { isPrint } = usePrintMode();
  const containerWidthStyle = computed(() => (isPrint.value ? '100%' : `${containerWidth.value}px`));
  const contentWidthStyle = computed(() => (isPrint.value ? '100%' : `${contentWidth.value}px`));

  const handleDragStart = (event) => {
    event.stopPropagation();
    event.preventDefault();

    document.body.classList.add('no-interaction');

    draggingWidth.value = savedSidebarWidth.value;
    dragging.value = true;
    sidebarStartX.value = event.clientX;
  };

  const handleDrag = (event) => {
    event.preventDefault();
    event.stopPropagation();

    if (!dragging.value) {
      return;
    }

    const update = () => {
      const delta = event.clientX - sidebarStartX.value;
      draggingWidth.value = savedSidebarWidth.value + delta;
    };

    debounce(update, 5);
  };

  const handleDragEnd = () => {
    if (!dragging.value) {
      return;
    }

    document.body.classList.remove('no-interaction');

    dragging.value = false;

    const finalWidth = constrainSidebarWidth(draggingWidth.value);
    updateSingle({
      uid: personalAppSettings.value.uid,
      [personalAppSettingsConfig.edges.mainSidebarWidth]: finalWidth,
    });
  };

  const setWindowWidth = () => {
    const update = () => {
      windowWidth.value = window.innerWidth;
    };
    debounce(update, 100);
  };

  onMounted(() => {
    window.addEventListener('resize', setWindowWidth);
  });

  onUnmounted(() => {
    window.removeEventListener('resize', setWindowWidth);
  });

  watch(containerWidthWatch, (width) => {
    if (width < MIN_CONTENT_WIDTH) {
      if (dragging.value) {
        if (desktopSidebarHidden.value) {
          return;
        }
        EventBus.$emit(EVENTS.SIDEBAR.MINIMAL_CONTENT_WIDTH_REACHED, width);
        return;
      }
      overwriteDesktopSidebarHidden.value = true;
      return;
    }
    overwriteDesktopSidebarHidden.value = false;
  });

  watch(sidePeekActive, (active) => {
    if (!active) {
      overwriteDesktopSidebarHidden.value = false;
    }
  });

  return {
    desktopSidebarHidden,
    updateHideDesktopSideBar,
    containerWidth,
    containerWidthStyle,
    contentWidth,
    contentWidthStyle,
    sidebarWidth,
    dragging,
    handleDragStart,
    handleDrag,
    handleDragEnd,
  };
}
